import { useSpring, useSpringRef } from "@react-spring/web";

/**
 * @typedef {object} Timings
 * @property {{duration: number, delay: number}} timings.text
 * @property {{duration: number, delay: number}} timings.persona
 * @property {{duration: number, delay: number}} timings.texture
 * @property {{esDelay: number }} timings.options
 */

export function useAnimations() {
  const timings = {};
  timings.text = { duration: 1500, delay: 0 };
  timings.persona = { duration: 1000, delay: 1000 };
  timings.texture = { duration: 1500, delay: 0 };
  timings.options = { esDelay: 2000 };
  const {
    ref: textEnRef,
    styles: textEnStyles,
    reset: textEnReset,
  } = useTextEnAnimation(timings);
  const {
    ref: textEsRef,
    styles: textEsStyles,
    reset: textEsReset,
  } = useTextEsAnimation(timings);
  const {
    ref: personaRef,
    styles: personaStyles,
    reset: personaReset,
  } = usePersonaAnimation(timings);
  const {
    ref: textureRef,
    styles: textureStyles,
    reset: textureReset,
  } = useTextureAnimation(timings);

  return {
    styles: {
      textEn: textEnStyles,
      textEs: textEsStyles,
      persona: personaStyles,
      texture: textureStyles,
    },
    intros: {
      textEn: textEnRef.start,
      persona: personaRef.start,
      texture: textureRef.start,
      textEs: textEsRef.start,
    },
    resets: {
      textEn: textEnReset,
      persona: personaReset,
      texture: textureReset,
      textEs: textEsReset,
    },
  };
}

/**
 * @param {Timings} timings
 */
function useTextEnAnimation(timings) {
  const ref = useSpringRef();
  const from = {
    opacity: 0,
  };
  const styles = useSpring({
    ref,
    opacity: 1,
    from,
    delay: timings.text.delay,
    config: { duration: timings.text.duration },
  });

  /**
   * @returns {Promise<boolean>}
   */
  function reset() {
    return new Promise((resolve) => {
      ref.start({ ...from, onRest: () => resolve(true) });
    });
  }

  return { ref, styles, reset };
}

/**
 * @param {Timings} timings
 */
function useTextEsAnimation(timings) {
  const ref = useSpringRef();
  const from = {
    opacity: 0,
  };
  const styles = useSpring({
    ref,
    opacity: 1,
    from,
    delay: timings.text.delay,
    config: { duration: timings.text.duration },
  });

  /**
   * @returns {Promise<boolean>}
   */
  function reset() {
    return new Promise((resolve) => {
      ref.start({ ...from, onRest: () => resolve(true) });
    });
  }

  return { ref, styles, reset };
}

/**
 * @param {Timings} timings
 */
function usePersonaAnimation(timings) {
  const ref = useSpringRef();
  const from = {
    y: 2000,
    opacity: 0,
  };
  const styles = useSpring({
    ref,
    y: 0,
    opacity: 1,
    from,
    delay: timings.persona.delay,
    config: { duration: timings.persona.duration },
  });

  /**
   * @returns {Promise<boolean>}
   */
  function reset() {
    return new Promise((resolve) => {
      ref.start({ ...from, onRest: () => resolve(true) });
    });
  }

  return { ref, styles, reset };
}

/**
 * @param {Timings} timings
 */
function useTextureAnimation(timings) {
  const ref = useSpringRef();
  const from = {
    opacity: 0,
  };
  const styles = useSpring({
    ref,
    opacity: 1,
    from,
    delay: timings.texture.delay,
    config: { duration: timings.texture.duration },
  });

  /**
   * @returns {Promise<boolean>}
   */
  function reset() {
    return new Promise((resolve) => {
      ref.start({ ...from, onRest: () => resolve(true) });
    });
  }
  return { ref, styles, reset };
}
